/**
 * ==========================================================================
 *  SURFACES: the palette, and the textures drawn from it
 * ==========================================================================
 *  Every material in the blockout is a flat colour plus a texture generated at
 *  load time onto a `<canvas>`. Nothing is fetched.
 *
 *  That is not a purity exercise, it is three separate constraints agreeing:
 *
 *    - the site ships under a `connect-src 'self'` CSP and has no asset
 *      pipeline, so a texture that arrives over the network is a texture that
 *      has to be hosted, versioned and invalidated alongside the physics;
 *    - image files carry licences, and a LAN party joke does not need a
 *      provenance audit;
 *    - the same seed draws the same pixels on every machine, so nobody's
 *      screen is quieter or busier than anybody else's.
 *
 *  Broad shapes keep the low-poly scene readable at speed; finer wood grain,
 *  weathering and needles give nearby surfaces detail without extra meshes.
 *
 *  Rendering only. Nothing in this file may ever be imported by `src/sim`.
 * ==========================================================================
 */

import * as THREE from 'three';
import { mulberry32 } from '../utils/prng';
import { SIZE, dot, grain, shade, shadeAlpha, soak, type Ctx, type Rng } from './canvas';
import type { MaterialKey } from '../world/Level';

export const PALETTE: Record<MaterialKey, number> = {
  stone: 0xa8a49c,
  water: 0x2f8fb0,
  tub: 0x6d4b30,
  hannu: 0xd06a63,
  // Whitewashed lime over stone, like the silo the tornimajoitus was built out of.
  tower: 0xd6d1c6,
  pirttiFloor: 0x67512f,
  pirttiWall: 0x9a7f58,
  pirttiRoof: 0x6b563e,
  grass: 0x51803c,
  gravel: 0x9c9384,
  fence: 0x6a5a45,
  wood: 0x8a6a45,
  furniture: 0xa9855c,
  fabric: 0x7d5f80,
  /**
   * The XLMOTO tent's canopy and the skirt hanging off it. Coated polyester,
   * and dark enough to read as the black it is printed as from across the yard
   * - but not black, for the same reason `tyre` is not: the canopy is the one
   * thing on the piha with the sky behind it, and a true black up there is a
   * hole cut in the morning.
   */
  tentCanopy: 0x26262c,
  /** The plain sheeting zipped in under it, which is unprinted white PE. */
  tentWall: 0xd8d7d1,
  /** Cheap red polypropylene - the one thing in the pirtti that is not wood. */
  bucket: 0xb63a2c,
  /**
   * What last night left behind, in the bucket and on the mattress. One colour
   * for both, because it was one night: two carefully distinguished shades of
   * it would be a level of research nobody asked for.
   */
  stain: 0xb09549,
  metal: 0x828a94,
  /**
   * Plated cup metal, and the only gold in the level. Its own key rather than
   * `goal`, which is the yellow the finish is painted in: those two are never
   * meant to move together, and a trophy is not a checkered flag.
   */
  brass: 0xd4a437,
  /**
   * Kamiina steel. Sootier and far duller than the galvanised sheet the rest of
   * the yard's metal is, because this one has spent a season being lit outdoors
   * - but still not black, for the reason below.
   */
  stove: 0x413c38,
  /** Rubber. Dark, but never black - a true black reads as a hole in the mesh. */
  tyre: 0x1e1e22,
  /** Lit plastic: tail lights, indicators, the charge point's status lamp. */
  lamp: 0xc0392b,
  /**
   * Fire, seen through the gap round a kamiina hatch. Drawn unlit like the
   * monitors are - a flame that takes the morning's light on it is not a flame.
   */
  ember: 0xff7b2e,
  car: 0x9aa3ad,
  carAlt: 0x8c4b3c,
  // The newest thing in the yard, and it needs to read as such from a distance:
  // the player has to know which car is the one with a cable attached to it.
  carEv: 0xe3e8ec,
  carGlass: 0x2f3d4a,
  /**
   * The pane in Einon pirtti's window, which is a different problem from a
   * windscreen: a car's glass is looked *at* from outside and is allowed to be
   * a dark tinted slab, and this one is looked *through* on the way out. So it
   * is the cold morning sky rather than the shade of anything, drawn at a low
   * opacity in `World.buildIkkuna` - and the same colour does the shards,
   * because they are the same glass a tenth of a second later.
   */
  windowGlass: 0xc4dcea,
  cable: 0x1b1d22,
  lanFloor: 0x474d58,
  lanWall: 0x656d80,
  lanRoof: 0x4c545f,
  // The juhlanavetta from outside: rendered concrete block under a brown metal
  // roof, with the red-and-white trim every building in the pihapiiri wears.
  barnWall: 0xb9b3a8,
  barnRoof: 0x6b6157,
  faluRed: 0x8f3f30,
  porcelain: 0xeef1ee,
  /** Bare earth worn through the yard's gravel where everybody runs. */
  dirt: 0x7e7060,
  table: 0xb0a48c,
  sleeper: 0xc0524a,
  goal: 0xffd25a,
  screen: 0x59d6ff,
  /**
   * The plastic round a panel, which is nearly black and never quite. Its own
   * key rather than `screen`, because `screen` is what a lit thing is drawn in
   * - the charge lights, the projector's lens - and a monitor stopped being a
   * lit thing the moment there was a picture on it. See `src/render/screens.ts`.
   */
  monitor: 0x24272d,
  /**
   * Beer in a glass, seen from across a room: amber and a good deal darker
   * than the bottle in your hand, because a tuoppi is 14 cm of it end to end
   * and the light in the juhlasali is what it is at nine in the morning.
   */
  beer: 0xc8871c,
  /**
   * The part of a tuoppi that has nothing in it. Cold and nearly colourless
   * where `beer` is warm, and a good deal darker than `porcelain` on purpose:
   * the empty half of a glass must never be mistaken for the head on a full
   * one, and across a dim hall those are the only two pale things on a table.
   */
  glass: 0xb6c2c6,
  /**
   * The same beer once it is out of the glass: a millimetre of it spread over a
   * floor is paler and duller than 14 cm of it in a tuoppi, and it has to read
   * as spilled from standing height rather than as a rug somebody laid there.
   */
  spill: 0xd0a04a,
  door: 0xa3703f,
  foliage: 0x35662f,
};

/** One generated surface, plus the scale it was drawn to be seen at. */
export interface Surface {
  map: THREE.Texture;
  /**
   * How many metres of world one tile covers. The caller scales UVs by this,
   * which is why a 30 m barn wall and a 2 m woodpile show the same size blocks
   * instead of the same *number* of them.
   */
  worldSize: number;
}

/* ------------------------------------------------------------- recipes --- */

/** Loose material: gravel, grass, earth. Base colour plus a lot of specks. */
function speckle(count: number, muls: number[], rMin: number, rMax: number) {
  return (ctx: Ctx, rng: Rng, base: number): void => {
    ctx.fillStyle = shade(base);
    ctx.fillRect(0, 0, SIZE, SIZE);
    for (let i = 0; i < count; i++) {
      ctx.fillStyle = shade(base, muls[(rng() * muls.length) | 0]);
      dot(ctx, rng() * SIZE, rng() * SIZE, rMin + rng() * (rMax - rMin));
    }
  };
}

/** Sawn boards, running across the tile. `count` boards per tile. */
function planks(count: number, vertical: boolean, grainStrokes = 60) {
  return (ctx: Ctx, rng: Rng, base: number): void => {
    ctx.fillStyle = shade(base);
    ctx.fillRect(0, 0, SIZE, SIZE);
    const pitch = SIZE / count;
    for (let i = 0; i < count; i++) {
      const at = i * pitch;
      // Each board is cut from a slightly different part of the log.
      ctx.fillStyle = shade(base, 0.88 + rng() * 0.26);
      if (vertical) ctx.fillRect(at, 0, pitch - 1, SIZE);
      else ctx.fillRect(0, at, SIZE, pitch - 1);
      // The shadow line in the joint.
      ctx.fillStyle = shade(base, 0.55);
      if (vertical) ctx.fillRect(at + pitch - 1.5, 0, 1.5, SIZE);
      else ctx.fillRect(0, at + pitch - 1.5, SIZE, 1.5);
      ctx.fillStyle = shade(base, 1.18);
      if (vertical) ctx.fillRect(at, 0, 0.7, SIZE);
      else ctx.fillRect(0, at, SIZE, 0.7);
    }
    // The zero-grain variant also draws welded tent panels, which have no knots.
    if (grainStrokes === 0) return;
    // Grain, along the length of the boards.
    ctx.lineWidth = 1;
    for (let i = 0; i < grainStrokes; i++) {
      ctx.strokeStyle = shade(base, 0.78 + rng() * 0.1);
      const at = rng() * SIZE;
      const from = rng() * SIZE;
      const length = SIZE * (0.15 + rng() * 0.5);
      const wave = rng() * 1.8;
      // Wrap the stroke at both ends so grain does not stop at tile boundaries.
      for (const offset of [-SIZE, 0]) {
        ctx.beginPath();
        for (let step = 0; step <= 12; step++) {
          const along = from + offset + length * step / 12;
          const across = at + Math.sin(step * 0.5) * wave;
          const x = vertical ? across : along;
          const y = vertical ? along : across;
          if (step === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
    }
    for (let board = 0; board < count; board++) {
      const across = (board + 0.3 + rng() * 0.4) * pitch;
      const along = 30 + rng() * (SIZE - 60);
      ctx.save();
      ctx.translate(vertical ? across : along, vertical ? along : across);
      if (vertical) ctx.rotate(Math.PI / 2);
      for (let ring = 3; ring >= 1; ring--) {
        ctx.strokeStyle = shade(base, 0.68 + ring * 0.08);
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.ellipse(0, 0, ring * 4, ring * pitch * 0.07, 0, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.fillStyle = shade(base, 0.6);
      ctx.beginPath();
      ctx.ellipse(0, 0, 3, pitch * 0.055, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  };
}

/** Laid slabs, with grout between them. */
function tiles(cols: number, rows: number) {
  return (ctx: Ctx, rng: Rng, base: number): void => {
    ctx.fillStyle = shade(base, 0.62);
    ctx.fillRect(0, 0, SIZE, SIZE);
    const w = SIZE / cols;
    const h = SIZE / rows;
    for (let cx = 0; cx < cols; cx++) {
      for (let cy = 0; cy < rows; cy++) {
        ctx.fillStyle = shade(base, 0.88 + rng() * 0.24);
        ctx.fillRect(cx * w + 1.5, cy * h + 1.5, w - 3, h - 3);
      }
    }
  };
}

/** Rendered concrete block, laid in running bond. */
function blocks(cols: number, rows: number) {
  return (ctx: Ctx, rng: Rng, base: number): void => {
    ctx.fillStyle = shade(base, 0.78);
    ctx.fillRect(0, 0, SIZE, SIZE);
    const w = SIZE / cols;
    const h = SIZE / rows;
    for (let cy = 0; cy < rows; cy++) {
      // Every other course is offset by half a block.
      const offset = cy % 2 === 0 ? 0 : w / 2;
      for (let cx = -1; cx <= cols; cx++) {
        ctx.fillStyle = shade(base, 0.94 + rng() * 0.12);
        ctx.fillRect(cx * w + offset + 1, cy * h + 1, w - 2, h - 2);
        ctx.fillStyle = shade(base, 1.08);
        ctx.fillRect(cx * w + offset + 1, cy * h + 1, w - 2, 0.8);
      }
    }
    weathering(ctx, rng, base, 45, 0.16);
  };
}

/** Profiled sheet roofing: the ribs are what make a roof read as a roof. */
function ribs(count: number) {
  return (ctx: Ctx, rng: Rng, base: number): void => {
    const pitch = SIZE / count;
    for (let i = 0; i < count; i++) {
      const at = i * pitch;
      const gradient = ctx.createLinearGradient(at, 0, at + pitch, 0);
      gradient.addColorStop(0, shade(base, 0.72));
      gradient.addColorStop(0.45, shade(base, 1.16));
      gradient.addColorStop(0.55, shade(base, 1.16));
      gradient.addColorStop(1, shade(base, 0.72));
      ctx.fillStyle = gradient;
      ctx.fillRect(at, 0, pitch, SIZE);
    }
    // Fixing screws down every second rib, so the scale reads.
    for (let i = 0; i < count; i += 2) {
      ctx.fillStyle = shade(base, 0.5);
      for (let y = 24; y < SIZE; y += 64) dot(ctx, i * pitch + pitch / 2, y, 1.6);
    }
    weathering(ctx, rng, base, 28, 0.13);
  };
}

/** Soft, wrapped mineral stains and patina, underneath the final fine grain. */
function weathering(ctx: Ctx, rng: Rng, base: number, count: number, opacity: number): void {
  for (let i = 0; i < count; i++) {
    const tone = i % 3 === 0 ? 1.35 : 0.55;
    soak(ctx, rng() * SIZE, rng() * SIZE, 7 + rng() * 28, [
      [0, shadeAlpha(base, tone, opacity)],
      [1, shadeAlpha(base, tone, 0)],
    ]);
  }
}

/** Woven cloth: sleeping bags, cushions, the rug. */
function weave(pitch: number) {
  return (ctx: Ctx, rng: Rng, base: number): void => {
    ctx.fillStyle = shade(base);
    ctx.fillRect(0, 0, SIZE, SIZE);
    for (let at = 0; at < SIZE; at += pitch) {
      ctx.fillStyle = shade(base, 1.14);
      ctx.fillRect(at, 0, pitch / 2, SIZE);
      ctx.fillStyle = shade(base, 0.86);
      ctx.fillRect(0, at, SIZE, pitch / 2);
    }
    void rng;
  };
}

/**
 * Something spilled into a fabric, spread, and dried where it stopped.
 *
 * Every blob is palest in the middle and darkest at its rim, which is the wrong
 * way round for a splash and the right way round for a soak: the tide line
 * where the wet edge dried is the whole of what makes a stain read as a stain.
 *
 * Kept deliberately low in contrast. An earlier pass drew the rims as hard
 * circles and the result was unmistakably a slice of cheese - and because the
 * patches this goes on are separate boxes, each with its own UV origin, any
 * pattern crisp enough to recognise also draws a seam along every join.
 */
function blotches(count: number) {
  return (ctx: Ctx, rng: Rng, base: number): void => {
    ctx.fillStyle = shade(base, 0.96);
    ctx.fillRect(0, 0, SIZE, SIZE);
    for (let i = 0; i < count; i++) {
      const radius = SIZE * (0.15 + rng() * 0.17);
      soak(ctx, rng() * SIZE, rng() * SIZE, radius, [
        [0, shadeAlpha(base, 1.14, 0.5)],
        [0.66, shadeAlpha(base, 1.02, 0.42)],
        [0.93, shadeAlpha(base, 0.74, 0.5)],
        [1, shadeAlpha(base, 0.74, 0)],
      ]);
    }
  };
}

/** Painted sheet metal: cars, appliances, the wheelbarrow. */
function brushed(sheen: number) {
  return (ctx: Ctx, rng: Rng, base: number): void => {
    const gradient = ctx.createLinearGradient(0, 0, 0, SIZE);
    gradient.addColorStop(0, shade(base, 1 + sheen));
    gradient.addColorStop(0.55, shade(base));
    gradient.addColorStop(1, shade(base, 1 - sheen * 0.7));
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, SIZE, SIZE);
    ctx.lineWidth = 1;
    for (let i = 0; i < 90; i++) {
      ctx.strokeStyle = shade(base, 0.96 + rng() * 0.08);
      const y = rng() * SIZE;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(SIZE, y);
      ctx.stroke();
    }
  };
}

/** Panel-sized paint: soft sky sheen, fine metallic flecks and road dust below. */
function carPaint(dust: number) {
  return (ctx: Ctx, rng: Rng, base: number): void => {
    ctx.fillStyle = shade(base);
    ctx.fillRect(0, 0, SIZE, SIZE);
    const sheen = ctx.createLinearGradient(0, 0, 0, SIZE);
    sheen.addColorStop(0, 'rgba(210,231,247,0.12)');
    sheen.addColorStop(0.32, 'rgba(226,238,247,0.24)');
    sheen.addColorStop(0.48, 'rgba(226,238,247,0)');
    sheen.addColorStop(0.72, `rgba(66,55,42,${dust * 0.2})`);
    sheen.addColorStop(1, `rgba(66,55,42,${dust})`);
    ctx.fillStyle = sheen;
    ctx.fillRect(0, 0, SIZE, SIZE);
    for (let i = 0; i < 650; i++) {
      const x = rng() * SIZE;
      const y = rng() * SIZE;
      ctx.fillStyle = i % 3 === 0 ? 'rgba(235,240,244,0.14)' : 'rgba(50,43,34,0.12)';
      ctx.fillRect(x, y, 0.7, 0.7);
    }
    for (let i = 0; i < 180; i++) {
      ctx.fillStyle = `rgba(109,94,70,${dust * rng()})`;
      dot(ctx, rng() * SIZE, SIZE * (0.76 + rng() * 0.24), 0.5 + rng() * 1.2);
    }
  };
}

/** Baked sky and treeline reflection; no scene capture or extra render pass. */
function carGlass(ctx: Ctx, rng: Rng, base: number): void {
  const sky = ctx.createLinearGradient(0, 0, 0, SIZE);
  sky.addColorStop(0, '#92afc3');
  sky.addColorStop(0.45, '#586f82');
  sky.addColorStop(0.6, shade(base, 1.12));
  sky.addColorStop(1, shade(base, 0.65));
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, SIZE, SIZE);
  ctx.fillStyle = 'rgba(19,37,39,0.2)';
  ctx.beginPath();
  ctx.moveTo(0, SIZE);
  for (let x = 0; x <= SIZE; x += 8) ctx.lineTo(x, 140 - rng() * 38);
  ctx.lineTo(SIZE, SIZE);
  ctx.fill();
  ctx.fillStyle = 'rgba(221,238,247,0.12)';
  ctx.beginPath();
  ctx.moveTo(35, 0);
  ctx.lineTo(68, 0);
  ctx.lineTo(198, SIZE);
  ctx.lineTo(181, SIZE);
  ctx.fill();
}

/** Circumferential grooves and alternating tread blocks. */
function rubber(ctx: Ctx, rng: Rng, base: number): void {
  brushed(0.08)(ctx, rng, base);
  for (let row = 0; row < 4; row++) {
    const y = row * SIZE / 4;
    ctx.fillStyle = shade(base, 0.55);
    ctx.fillRect(0, y, SIZE, 3);
    for (let x = -16; x < SIZE; x += 32) {
      ctx.strokeStyle = shade(base, 0.5);
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(x, y + 4);
      ctx.lineTo(x + (row % 2 ? 14 : -14), y + SIZE / 4 - 4);
      ctx.stroke();
    }
  }
}

/** Standing water, seen from above. */
function ripples(): (ctx: Ctx, rng: Rng, base: number) => void {
  return (ctx, rng, base) => {
    ctx.fillStyle = shade(base);
    ctx.fillRect(0, 0, SIZE, SIZE);
    weathering(ctx, rng, base, 38, 0.24);
    ctx.lineWidth = 1.1;
    for (let i = 0; i < 26; i++) {
      ctx.strokeStyle = shade(base, 1.08 + rng() * 0.24);
      const y = rng() * SIZE;
      const amplitude = 2 + rng() * 5;
      for (const offset of [-SIZE, 0, SIZE]) {
        ctx.beginPath();
        for (let x = 0; x <= SIZE; x += 4) {
          const wy = y + offset + Math.sin((x / SIZE) * Math.PI * 4 + i) * amplitude;
          if (x === 0) ctx.moveTo(x, wy);
          else ctx.lineTo(x, wy);
        }
        ctx.stroke();
      }
    }
  };
}

/** Layered spruce sprays: dark branch mass under pairs of sunlit needles. */
function leaves(count: number) {
  return (ctx: Ctx, rng: Rng, base: number): void => {
    speckle(160, [0.62, 0.78, 0.94, 1.1], 6, 18)(ctx, rng, base);
    for (let i = 0; i < count; i++) {
      const x = rng() * SIZE;
      const y = rng() * SIZE;
      const angle = (rng() - 0.5) * 1.8;
      const length = 10 + rng() * 20;
      const tone = 0.82 + rng() * 0.65;
      const wrap = (at: number) => at < 32 ? [0, SIZE] : at > SIZE - 32 ? [0, -SIZE] : [0];
      for (const dx of wrap(x)) for (const dy of wrap(y)) {
        ctx.save();
        ctx.translate(x + dx, y + dy);
        ctx.rotate(angle);
        ctx.strokeStyle = shade(base, tone);
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(0, length);
        for (let at = 2; at < length; at += 3) {
          const spread = (1 - at / length) * 8 + 1;
          ctx.moveTo(-spread, at - 4);
          ctx.lineTo(0, at + 3);
          ctx.lineTo(spread, at - 4);
        }
        ctx.stroke();
        ctx.restore();
      }
    }
  };
}

/* --------------------------------------------------------- the recipes --- */

interface Recipe {
  draw: (ctx: Ctx, rng: Rng, base: number) => void;
  worldSize: number;
  grain?: number;
}

/**
 * `worldSize` is the whole point of this table: it is the real size of one
 * tile, in metres. Getting it wrong is what makes a blockout look like a
 * blockout - a wall whose blocks are two metres across reads as a doll's house
 * no matter how well the blocks themselves are drawn.
 */
const RECIPES: Partial<Record<MaterialKey, Recipe>> = {
  grass: { draw: speckle(900, [0.78, 0.9, 1.14, 1.28], 1.5, 5), worldSize: 3.5, grain: 26 },
  gravel: { draw: speckle(1400, [0.7, 0.85, 1.12, 1.3], 1, 3.5), worldSize: 2.2, grain: 30 },
  dirt: { draw: speckle(700, [0.8, 0.92, 1.1], 2, 7), worldSize: 3.2, grain: 22 },
  stone: { draw: tiles(4, 4), worldSize: 2.4, grain: 16 },
  foliage: { draw: leaves(260), worldSize: 2.2, grain: 10 },

  wood: { draw: planks(6, false), worldSize: 2.0, grain: 14 },
  fence: { draw: planks(7, true), worldSize: 2.2, grain: 14 },
  door: { draw: planks(4, true), worldSize: 1.1, grain: 12 },
  furniture: { draw: planks(5, false), worldSize: 1.2, grain: 12 },
  table: { draw: planks(3, false, 30), worldSize: 1.6, grain: 10 },
  // Staves, standing up around the tub the way a barrel is built.
  tub: { draw: planks(14, true), worldSize: 1.8, grain: 14 },

  // Board siding. The pirtti and the aitat wear the same boards.
  pirttiWall: { draw: planks(8, false), worldSize: 2.4, grain: 12 },
  pirttiFloor: { draw: planks(6, false), worldSize: 2.6, grain: 12 },
  faluRed: { draw: planks(8, false), worldSize: 2.4, grain: 12 },
  // Profiled sheet, which is what every roof in the pihapiiri actually is.
  pirttiRoof: { draw: ribs(7), worldSize: 1.8, grain: 10 },
  barnRoof: { draw: ribs(7), worldSize: 1.8, grain: 10 },
  lanRoof: { draw: ribs(7), worldSize: 1.8, grain: 10 },

  barnWall: { draw: blocks(3, 6), worldSize: 2.4, grain: 14 },
  tower: { draw: blocks(3, 6), worldSize: 2.4, grain: 16 },
  lanWall: { draw: blocks(3, 6), worldSize: 2.6, grain: 12 },
  lanFloor: { draw: tiles(5, 5), worldSize: 2.0, grain: 12 },

  metal: { draw: brushed(0.1), worldSize: 1.4, grain: 10 },
  // The same rolled sheet as `metal` on a much smaller tile: a kamiina is 60 cm
  // across, and a 1.4 m tile would put a quarter of one highlight on the whole
  // stove. Heavy grain, because on this thing the grain is soot.
  stove: { draw: brushed(0.06), worldSize: 0.7, grain: 18 },
  monitor: { draw: brushed(0.08), worldSize: 0.6, grain: 8 },
  tyre: { draw: rubber, worldSize: 0.8, grain: 8 },
  car: { draw: carPaint(0.22), worldSize: 2.6, grain: 3 },
  carAlt: { draw: carPaint(0.3), worldSize: 2.6, grain: 3 },
  carEv: { draw: carPaint(0.08), worldSize: 2.6, grain: 2 },
  carGlass: { draw: carGlass, worldSize: 2.6, grain: 2 },

  fabric: { draw: weave(8), worldSize: 1.0, grain: 14 },
  // Coated tent sheeting. `planks` rather than `weave`, held vertically: PE
  // sheet is smooth, and what you actually see on one is the welded seam every
  // half metre where two panels were joined - which is a board's shadow line
  // and not a weave. A cloth recipe here came out as mosquito netting at
  // arm's length, and this is the one surface in the yard you run along.
  tentCanopy: { draw: planks(6, true, 0), worldSize: 2.6, grain: 5 },
  tentWall: { draw: planks(6, true, 0), worldSize: 2.6, grain: 5 },
  // Moulding ribs down a bucket wall, which is what `brushed` draws if you
  // hold it sideways - the cylinder's UVs run once around, not once up.
  bucket: { draw: brushed(0.2), worldSize: 0.55, grain: 6 },
  // A far smaller tile than anything else here, because the surfaces this goes
  // on are 30-70 cm across: at any larger size each patch would show one
  // recognisable shape, and the shapes would not line up across the joins.
  stain: { draw: blotches(11), worldSize: 0.5, grain: 14 },
  water: { draw: ripples(), worldSize: 3.0, grain: 3 },
  // Water's recipe on a sixth of its tile: the palju is three metres across and
  // a puddle is half of one, so at the pond's tile a whole spill would be a
  // single ripple and would not read as liquid at all.
  spill: { draw: ripples(), worldSize: 0.5, grain: 6 },
  porcelain: { draw: brushed(0.05), worldSize: 1.2, grain: 5 },
};

/* ---------------------------------------------------------------- build -- */

/**
 * Draws every recipe once.
 *
 * Seeded per material rather than from one running stream, so adding a texture
 * cannot change how any of the others look.
 */
export function buildSurfaces(anisotropy = 1): Partial<Record<MaterialKey, Surface>> {
  const out: Partial<Record<MaterialKey, Surface>> = {};

  let seed = 0x1a51aa;
  for (const [key, recipe] of Object.entries(RECIPES) as [MaterialKey, Recipe][]) {
    const canvas = document.createElement('canvas');
    canvas.width = SIZE;
    canvas.height = SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) continue;

    const rng = mulberry32((seed += 0x9e37));
    recipe.draw(ctx, rng, PALETTE[key]);
    if (recipe.grain) grain(ctx, rng, recipe.grain);

    const map = new THREE.CanvasTexture(canvas);
    map.colorSpace = THREE.SRGBColorSpace;
    map.wrapS = THREE.RepeatWrapping;
    map.wrapT = THREE.RepeatWrapping;
    // The yard is mostly seen at a glancing angle at speed, which is the exact
    // case trilinear filtering turns into mush. Anisotropy is worth the cost.
    map.anisotropy = anisotropy;
    out[key] = { map, worldSize: recipe.worldSize };
  }

  return out;
}
